/**
 * @voltxampere/engine — Trapezoidal companion models (SPECS §3.8)
 *
 * TRAP is the SPECS-default integrator (`method = "trap"`) and the
 * one most users hit first. It is **2nd order accurate** and
 * **A-stable** but not L-stable, so very stiff problems can ring;
 * the adaptive step controller catches that by rejecting steps and
 * shrinking h until the LTE budget is met.
 *
 * Capacitor:
 *
 *   i_C(t) = (2C/h)·(V_C(t) − V_C(t−h)) − i_C(t−h)
 *          = Geq · V_C(t) + Ieq
 *   Geq    = 2C/h
 *   Ieq    = −Geq · V_C(t−h) − i_C(t−h)
 *
 * Inductor (dual):
 *
 *   V_L(t) = (2L/h)·(I_L(t) − I_L(t−h)) − V_L(t−h)
 *          = Geq · I_L(t) + Ieq
 *   Geq    = 2L/h
 *   Ieq    = −Geq · I_L(t−h) − V_L(t−h)
 *
 * Note the asymmetry vs BE: TRAP needs `i_C(t−h)` for the capacitor
 * Ieq term (and `V_L(t−h)` for the inductor). Those are *not*
 * derivable from V/I node history alone — the solver must persist
 * them across accepted steps and feed them in here.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Capacitor companion under the Trapezoidal rule.
 *
 * @param C        capacitance in farads (> 0).
 * @param h        step size in seconds (> 0).
 * @param VCPrev   capacitor voltage at the previous accepted step.
 * @param ICPrev   capacitor current at the previous accepted step
 *                 (sign convention: positive pos → neg).
 */
export function capacitorTRAP(C, h, VCPrev, ICPrev) {
    if (!(C > 0) || !Number.isFinite(C)) {
        throw new ContractViolationError(`capacitorTRAP: C must be a positive finite number, got ${String(C)}`);
    }
    if (!(h > 0) || !Number.isFinite(h)) {
        throw new ContractViolationError(`capacitorTRAP: h must be a positive finite number, got ${String(h)}`);
    }
    if (!Number.isFinite(VCPrev)) {
        throw new ContractViolationError(`capacitorTRAP: VCPrev must be finite, got ${String(VCPrev)}`);
    }
    if (!Number.isFinite(ICPrev)) {
        throw new ContractViolationError(`capacitorTRAP: ICPrev must be finite, got ${String(ICPrev)}`);
    }
    const Geq = (2 * C) / h;
    const Ieq = -Geq * VCPrev - ICPrev;
    return { Geq, Ieq };
}
/**
 * Inductor companion under the Trapezoidal rule.
 *
 * @param L        inductance in henries (> 0).
 * @param h        step size in seconds (> 0).
 * @param ILPrev   inductor branch current at the previous accepted
 *                 step (sign: positive pos → neg).
 * @param VLPrev   inductor branch voltage at the previous accepted
 *                 step (V_pos − V_neg from xPrev's node difference).
 */
export function inductorTRAP(L, h, ILPrev, VLPrev) {
    if (!(L > 0) || !Number.isFinite(L)) {
        throw new ContractViolationError(`inductorTRAP: L must be a positive finite number, got ${String(L)}`);
    }
    if (!(h > 0) || !Number.isFinite(h)) {
        throw new ContractViolationError(`inductorTRAP: h must be a positive finite number, got ${String(h)}`);
    }
    if (!Number.isFinite(ILPrev)) {
        throw new ContractViolationError(`inductorTRAP: ILPrev must be finite, got ${String(ILPrev)}`);
    }
    if (!Number.isFinite(VLPrev)) {
        throw new ContractViolationError(`inductorTRAP: VLPrev must be finite, got ${String(VLPrev)}`);
    }
    const Geq = (2 * L) / h;
    const Ieq = -Geq * ILPrev - VLPrev;
    return { Geq, Ieq };
}
//# sourceMappingURL=trapezoidal.js.map